import axios from "../utils/AxiosInstance";
import { ResponseMessage } from "../models/ResponseMessage";
import { Constants } from "../constants/constants";
import {
  CreateOrganizationRequest,
  OrganizationListResult,
  OrganizationResponse,
  UpdateOrganizationRequest,
} from "../models/OrganizationModels";
import { PublicWebsiteResponse } from "../models/WebsiteModels";

const API_BASE_URL = Constants.API_BASE_URL;

export async function getOrganizationsAsync(
  token: string,
  page: number,
  pageSize: number,
  search?: string,
): Promise<ResponseMessage<OrganizationListResult>> {
  const response = await axios.get(`${API_BASE_URL}/organizations`, {
    params: { page, pageSize, search: search || undefined },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return ResponseMessage.fromJson(response.data);
}

export async function getOrganizationByIdAsync(
  token: string,
  id: string,
): Promise<ResponseMessage<OrganizationResponse>> {
  const response = await axios.get(`${API_BASE_URL}/organizations/${id}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return ResponseMessage.fromJson(response.data);
}

export async function getPublicWebsiteAsync(
  slug: string,
): Promise<ResponseMessage<PublicWebsiteResponse>> {
  const response = await axios.get(
    `${API_BASE_URL}/organizations/slug/${slug}/website`,
  );

  return ResponseMessage.fromJson(response.data);
}

const appendOrgFields = (
  data: FormData,
  org: CreateOrganizationRequest | UpdateOrganizationRequest,
) => {
  const appendNumber = (key: string, value?: number) =>
    data.append(key, value === undefined ? "" : String(value));
  const appendString = (key: string, value?: string) =>
    data.append(key, value ?? "");

  appendString("name", org.name);
  appendString("slug", org.slug);
  appendNumber("organizationType", org.organizationType);
  appendString("phone", org.phone);
  appendString("email", org.email);
  appendString("description", org.description);
  appendString("address", org.address);
  appendString("city", org.city);
  appendString("area", org.area);
  appendNumber("latitude", org.latitude);
  appendNumber("longitude", org.longitude);
  appendString("googleMapsUrl", org.googleMapsUrl);
  data.append("isActive", String(org.isActive ?? true));
  appendString("facebookUrl", org.facebookUrl);
  appendString("instagramUrl", org.instagramUrl);
  appendString("youtubeUrl", org.youtubeUrl);
  appendString("linkedInUrl", org.linkedInUrl);
  appendString("tiktokUrl", org.tiktokUrl);
  appendString("websiteTitle", org.websiteTitle);
  appendString("websiteTagline", org.websiteTagline);

  if (org.logoFile) data.append("logoFile", org.logoFile);
  if (org.bannerFile) data.append("bannerFile", org.bannerFile);
};

export async function createOrganizationAsync(
  token: string,
  organization: CreateOrganizationRequest,
): Promise<ResponseMessage<OrganizationResponse>> {
  const data = new FormData();
  appendOrgFields(data, organization);

  const response = await axios.post(`${API_BASE_URL}/organizations`, data, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return ResponseMessage.fromJson(response.data);
}

export async function updateOrganizationAsync(
  token: string,
  id: string,
  organization: UpdateOrganizationRequest,
): Promise<ResponseMessage<OrganizationResponse>> {
  const data = new FormData();
  appendOrgFields(data, organization);

  const response = await axios.put(`${API_BASE_URL}/organizations/${id}`, data, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return ResponseMessage.fromJson(response.data);
}

export async function deleteOrganizationAsync(
  token: string,
  id: string,
): Promise<ResponseMessage<boolean>> {
  const response = await axios.delete(`${API_BASE_URL}/organizations/${id}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return ResponseMessage.fromJson(response.data);
}